import { FnEditorElementType } from "./types";
export default class FnEditorElement {
    constructor(id, text, metaData) {
        this.id = id;
        this.metaData = metaData;
        this.text = '';
        this.intrinsicType = FnEditorElementType.Blank;
        this.type = FnEditorElementType.Blank;
        this.hasEmphasis = false;
        this.updateText(text);
    }
    evaluateIntrinsicType() {
        const textToEvaluate = this.text.trimStart();
        if (textToEvaluate === '' && this.text !== '  ') {
            return FnEditorElementType.Blank;
        }
        if (textToEvaluate === '===') {
            return FnEditorElementType.PageBreak;
        }
        if (textToEvaluate.startsWith('!')) {
            return FnEditorElementType.Action;
        }
        if (textToEvaluate.startsWith('~')) {
            return FnEditorElementType.Lyrics;
        }
        if (textToEvaluate.startsWith('@')) {
            return FnEditorElementType.Character;
        }
        if (textToEvaluate.startsWith('(') && this.text.endsWith(')')) {
            return FnEditorElementType.Parenthetical;
        }
        if (textToEvaluate.startsWith('>') && this.text.endsWith('<')) {
            return FnEditorElementType.CenteredAction;
        }
        if (textToEvaluate.startsWith('>')) {
            return FnEditorElementType.Transition;
        }
        if (textToEvaluate.startsWith('=')) {
            return FnEditorElementType.Synopsis;
        }
        if (textToEvaluate.startsWith('#')) {
            return FnEditorElementType.Section;
        }
        if (textToEvaluate.match(/^\.[\p{L}\p{N}]/gu)) {
            return FnEditorElementType.SceneHeading;
        }
        if (textToEvaluate.match(/^(int(\/ext)?|ext|est|i\/e)[ .]/i)) {
            return FnEditorElementType.SceneHeading;
        }
        if (textToEvaluate.length > 1 && textToEvaluate.match(/^[\p{Lu}\p{N}]+(?: [\p{Lu}\p{N}]+)* ?(\([^\(\)]*$)?(\([^\(\)]*\))?( *\^)?$/gu)) {
            return FnEditorElementType.Character;
        }
        if (textToEvaluate.match(/[\p{L} ]+TO:$/gu)) {
            return FnEditorElementType.Transition;
        }
        return FnEditorElementType.Action;
    }
    updateText(text) {
        const hadEmphasis = this.hasEmphasis;
        const changed = this.text !== text;
        this.text = text.replace(/\u00A0/g, ' '); // non breaking spaces to spaces
        this.hasEmphasis = this.textHasEmphasis(this.text);
        const typeBefore = this.intrinsicType;
        this.intrinsicType = this.evaluateIntrinsicType();
        let renderNeeded = false;
        if (hadEmphasis || this.hasEmphasis) {
            renderNeeded = true;
        }
        return {
            intrinsicTypeAfter: this.intrinsicType,
            intrinsicTypeBefore: typeBefore,
            renderNeeded,
            textChanged: changed
        };
    }
    evaluateType(previousType, nextElementIntrinsicType) {
        const before = this.type;
        this.type = this.intrinsicType;
        if (this.intrinsicType === FnEditorElementType.Parenthetical) {
            if (![FnEditorElementType.Dialogue, FnEditorElementType.Character].includes(previousType)) {
                this.type = FnEditorElementType.Action;
            }
        }
        else if (this.intrinsicType === FnEditorElementType.Action) {
            if ([FnEditorElementType.Parenthetical, FnEditorElementType.Character, FnEditorElementType.Dialogue].includes(previousType)) {
                this.type = FnEditorElementType.Dialogue;
            }
        }
        else if (this.intrinsicType === FnEditorElementType.Character) {
            if (previousType === FnEditorElementType.Character || previousType === FnEditorElementType.Parenthetical) {
                this.type = FnEditorElementType.Dialogue;
            }
            else if (nextElementIntrinsicType === FnEditorElementType.Blank) {
                this.type = FnEditorElementType.Action;
            }
        }
        else if (this.intrinsicType === FnEditorElementType.SceneHeading || this.intrinsicType === FnEditorElementType.Transition) {
            if (previousType !== FnEditorElementType.Blank || nextElementIntrinsicType !== FnEditorElementType.Blank) {
                this.type = FnEditorElementType.Action;
            }
        }
        return {
            typeBefore: before,
            typeAfter: this.type,
        };
    }
    textHasEmphasis(text) {
        const markdownPattern = /(\*\*\*.*?\*\*\*)|(\*\*.*?\*\*)|(\*.*?\*)|(\[\[)|(_.*?_)/;
        return markdownPattern.test(text) || /  /.test(text) || / $/.test(text); // test double spacing also
    }
    getId() {
        return this.id;
    }
    getText() {
        return this.text;
    }
    getIntrinsicType() {
        return this.intrinsicType;
    }
    getType() {
        return this.type;
    }
    syncWithDom(domElement) {
        domElement.classList.add('fn-editor-element');
        this.syncText(domElement);
    }
    syncType(domElement) {
        domElement.className = '';
        domElement.classList.add('fn-editor-element', 'fn-editor-element--' + this.type, 'fn-editor-element-intrinsic--' + this.intrinsicType);
    }
    syncText(domElement) {
        domElement.innerHTML = this.renderText(this.text);
    }
    escapeHTML(unsafeString) {
        const div = document.createElement('div');
        div.appendChild(document.createTextNode(unsafeString));
        return div.innerHTML;
    }
    renderText(text) {
        const spacedText = text
            .replace(/ $/g, `\u00A0`)
            .replace(/ (\s+)/g, function (match, p1) {
            return ' ' + p1.replace(/ /g, '\u00A0'); // replace all the following spaces with non-breaking space
        });
        let html = this.escapeHTML(spacedText)
            .replace(/\*\*\*(.+?)\*\*\*/g, '<span class="em-char">starstarstar</span><span class="emphasis--bold-italics">$1</span><span class="em-char">starstarstar</span>')
            .replace(/\*\*(.+?)\*\*/g, '<span class="em-char">starstar</span><span class="emphasis--bold">$1</span><span class="em-char">starstar</span>')
            .replace(/\*(.+?)\*/g, '<span class="em-char">star</span><span class="emphasis--italics">$1</span><span class="em-char">star</span>')
            .replace(/_(.+?)_/g, '<span class="em-char">_</span><span class="emphasis--underline">$1</span><span class="em-char">_</span>')
            .replace(/\[\[(.*?)\]\]/g, '<span class="em-char">[[</span><span class="emphasis--note">$1</span><span class="em-char">]]</span>')
            .replace(/<span class="em-char">(star)(star)?(star)?<\/span>/g, (...args) => {
            return `<span class="em-char">${args[1] ? '*' : ''}${args[2] ? '*' : ''}${args[3] ? '*' : ''}</span>`;
        });
        if (html.charAt(0) === '=') {
            html = '<span class="synopsis-indicator">=</span>' + html.substring(1);
        }
        return html;
    }
}
